/**
 * The six time controls the lobby offers, and the pure helpers around them.
 *
 * Moved out of `QuickPlay/` (M1a) so both lobby variants read ONE list: the
 * classic tiles and the gallery pills must never disagree about what is on
 * offer or what a beginner may not have.
 */
export type TimeControlOption = {
  tcMs: number;
  incMs: number;
  /** the old panel's animation key (GameSearch L46-53) */
  icon: 'bullet' | 'blitz' | 'rapid' | 'classical';
};

export const TIME_CONTROLS: TimeControlOption[] = [
  {tcMs: 120000, incMs: 0, icon: 'bullet'},
  {tcMs: 180000, incMs: 2000, icon: 'blitz'},
  {tcMs: 300000, incMs: 3000, icon: 'blitz'},
  {tcMs: 600000, incMs: 5000, icon: 'rapid'},
  {tcMs: 900000, incMs: 10000, icon: 'rapid'},
  {tcMs: 1500000, incMs: 10000, icon: 'classical'},
];

export const DEFAULT_TC = {tcMs: 300000, incMs: 3000};

/**
 * fix Z3: whether (tcMs, incMs) is one of the six chips this lobby actually
 * offers — `lobbyStore.hydrate` and `setTab` both use this to decide whether
 * a stored/left-over control is safe to show selected, rather than adopting
 * ANY number a prior write (a Play & Learn game's fixed 60+15, or anything
 * else stale) happened to leave behind.
 */
export const isOfferedTimeControl = (tcMs: number, incMs: number): boolean =>
  TIME_CONTROLS.some(tc => tc.tcMs === tcMs && tc.incMs === incMs);

/** "5 + 3" — minutes + seconds, the way the pills and the CTA read it. */
export const tcLabel = (tcMs: number, incMs: number): string =>
  `${Math.round(tcMs / 60000)} + ${Math.round(incMs / 1000)}`;

/** "5+3" — the old tile's own label (TimeControl L131). */
export const tcLabelTight = (tcMs: number, incMs: number): string =>
  `${Math.round(tcMs / 60000)}+${Math.round(incMs / 1000)}`;

/**
 * The two fastest controls a beginner is not offered — TimeControl/index.tsx
 * L40-42 returns null for `control === '2+0' || control === '3+2'`, which is
 * these two entries.
 */
const BEGINNER_HIDDEN: Array<{tcMs: number; incMs: number}> = [
  {tcMs: 120000, incMs: 0},
  {tcMs: 180000, incMs: 2000},
];

export const visibleTimeControls = (isBeginner: boolean): TimeControlOption[] =>
  isBeginner
    ? TIME_CONTROLS.filter(
        tc =>
          !BEGINNER_HIDDEN.some(
            hidden => hidden.tcMs === tc.tcMs && hidden.incMs === tc.incMs,
          ),
      )
    : TIME_CONTROLS;

/**
 * "3+2" (the dashboard's auto-join intent, GameSearch L46-53) -> ms. Only the
 * six controls we actually offer are honoured: `selectControl` falls through to
 * the FIRST entry on a miss, which would silently start a bullet game
 * (GameSearch L78-83), so an unknown value becomes the default instead.
 */
export const controlToMs = (
  control?: string | null,
): {tcMs: number; incMs: number} => {
  const [minutes, seconds] = String(control ?? '')
    .split('+')
    .map(part => Number(part.trim()));
  const match = TIME_CONTROLS.find(
    tc => tc.tcMs === minutes * 60000 && tc.incMs === seconds * 1000,
  );
  return match ? {tcMs: match.tcMs, incMs: match.incMs} : DEFAULT_TC;
};
